import React from "react";
import clsx from "clsx";
import styles from "./PricingSection.module.css";
import { Container } from "../../common/Container";
import { pricingData, heroData } from "../../../data/compassData";

const CheckIcon = () => (
  <svg viewBox="0 0 20 20" fill="currentColor" aria-hidden="true" className={styles.checkIcon}>
    <path
      fillRule="evenodd"
      d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z"
      clipRule="evenodd"
    />
  </svg>
);

const DashIcon = () => (
  <svg viewBox="0 0 20 20" fill="currentColor" aria-hidden="true" className={styles.dashIcon}>
    <rect x="5" y="9.25" width="10" height="1.5" rx="0.75" />
  </svg>
);

type Plan = typeof pricingData.free | typeof pricingData.premium;

const FeatureList: React.FC<{ plan: Plan }> = ({ plan }) => (
  <ul className={styles.features}>
    {plan.features.map((feature) => (
      <li
        key={feature.text}
        className={clsx(styles.feature, !feature.included && styles.featureExcluded)}
      >
        {feature.included ? <CheckIcon /> : <DashIcon />}
        <span>
          {feature.text}
          {!feature.included && <span className={styles.srOnly}> (not included)</span>}
        </span>
      </li>
    ))}
  </ul>
);

export const PricingSection: React.FC = () => {
  const { free, premium } = pricingData;

  return (
    <section id="pricing" className={styles.section} aria-labelledby="pricing-title">
      <Container>
        <div className={styles.header}>
          <h2 id="pricing-title" className={styles.title}>
            Free to start. Premium when you want more.
          </h2>
          <p className={styles.subtitle}>
            Try every Premium feature free for 14 days. No charge until the trial ends.
          </p>
        </div>

        <div className={styles.plans}>
          <div className={styles.plan}>
            <h3 className={styles.planName}>{free.name}</h3>
            <p className={styles.planDescription}>{free.description}</p>
            <p className={styles.price}>
              <span className={styles.amount}>{free.price}</span>
              <span className={styles.period}>{free.period}</span>
            </p>
            <p className={styles.alt}>No account needed</p>
            <FeatureList plan={free} />
            <a
              href={heroData.appStoreUrl}
              target="_blank"
              rel="noopener noreferrer"
              className={styles.ctaSecondary}
            >
              {free.cta}
            </a>
          </div>

          <div className={clsx(styles.plan, styles.planPremium)}>
            <h3 className={styles.planName}>{premium.name}</h3>
            <p className={styles.planDescription}>{premium.description}</p>
            <p className={styles.price}>
              <span className={styles.amount}>{premium.price}</span>
              <span className={styles.period}>{premium.period}</span>
            </p>
            <p className={styles.alt}>
              or {premium.yearlyPrice} ({premium.yearlySaving})
            </p>
            <FeatureList plan={premium} />
            <a
              href={heroData.appStoreUrl}
              target="_blank"
              rel="noopener noreferrer"
              className={styles.ctaPrimary}
            >
              {premium.cta}
            </a>
            <p className={styles.reassurance}>{premium.reassurance}</p>
          </div>
        </div>
      </Container>
    </section>
  );
};
